import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getRefForCheckout } from "@/lib/referrals";

/**
 * "Pay with PayPal" button block, rendered next to the existing Stripe
 * checkout on each listing. Loads the PayPal JS SDK only when the user is
 * actually on the listing page (lazy via the Provider), and mirrors the
 * Stripe checkout server flow:
 *   POST /api/payments/paypal/create-order  →  returns { order_id }
 *   POST /api/payments/paypal/capture/{id}  →  marks paid in Mongo
 */
const PayPalCheckoutButton = ({ listing, onSuccess, qty = 1, variantVid = null, shipCountry = null }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  if (!meta) {
    return (
      <div className="h-11 rounded-md border border-dashed border-black/15 flex items-center justify-center text-xs text-muted-foreground" data-testid="paypal-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin mr-2"/> Loading PayPal…
      </div>
    );
  }
  if (!meta.paypal_client_id) {
    return null; // PayPal not configured on the server — silently skip
  }

  const currency = (listing.currency || meta.platform_currency || "GBP").toUpperCase();
  const supported = new Set(["USD", "EUR", "GBP", "CAD", "AUD"]);
  const paypalCurrency = supported.has(currency) ? currency : "USD";

  return (
    <div data-testid="paypal-block" className="relative">
      <PayPalScriptProvider
        options={{
          clientId: meta.paypal_client_id,
          currency: paypalCurrency,
          intent: "capture",
          "disable-funding": "credit,card",
        }}
      >
        <PayPalButtons
          style={{ layout: "horizontal", color: "blue", shape: "rect", label: "paypal", height: 44, tagline: false }}
          fundingSource="paypal"
          createOrder={async () => {
            if (!user) {
              navigate("/login", { state: { from: `/listing/${listing.listing_id}` } });
              throw new Error("auth required");
            }
            if (listing.is_curated && !shipCountry) {
              toast.error("Please choose your shipping country first");
              throw new Error("ship country required");
            }
            try {
              const { data } = await api.post("/payments/paypal/create-order", {
                listing_id: listing.listing_id,
                origin_url: window.location.origin,
                referrer_id: getRefForCheckout(),
                qty: Math.max(1, Math.min(99, parseInt(qty, 10) || 1)),
                variant_vid: variantVid || null,
                ship_country: shipCountry || null,
              });
              return data.order_id;
            } catch (e) {
              toast.error(e.response?.data?.detail || "Couldn't start PayPal");
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              const { data: result } = await api.post(`/payments/paypal/capture/${data.orderID}`);
              if (result.ok) {
                toast.success("Payment received — seller has been notified.");
                onSuccess?.();
                navigate(`/payment/success?provider=paypal&order_id=${data.orderID}`);
              } else {
                toast.error(`Payment ${result.status?.toLowerCase() || "failed"}`);
              }
            } catch (e) {
              toast.error("Couldn't confirm the PayPal payment");
            }
          }}
          onError={(err) => {
            toast.error("PayPal error — try again or use Stripe");
            // eslint-disable-next-line no-console
            console.error("PayPal SDK error", err);
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckoutButton;
